import { isEmpty } from 'lodash';
import React from 'react';

import NoticeBlock from '../../Common/NoticeBlock';
import DataTable from '../../Fields/DataTable';

import ProcessingIndicator from '../../Common/ProcessingIndicator';

import {
  queryGetCreditCardFeePrice,
  queryGetFacilitySettings,
  queryListInvoices,
  querymakePayment,
  queryMyProfile,
} from '../../../Queries';

import { convertUnixMsToDateInTimezone } from '../../../Utils/date';
import { getFacility } from '../../../Utils/facility';
import ActionButton from '../../Common/ActionButton';
import '../Page.scss';
import './SubscriptionDetailHistoryPanel.scss';
import './SubscriptionProcessPayment.scss';
import CreditCardFeeDisclaimer from './CreditCardFeeDisclaimer';
import GPICreditCardForm from './GPI/GPICreditCardForm';

const invoiceQueryFields = [
  'total',
  'limit',
  'offset',
  {
    invoices: [
      'id',
      'readable_id',
      'amount',
      'balance',
      'created_at',
      {
        payor: [
          'id',
          'readable_id',
          'billing_firstname',
          'billing_lastname',
          'info_name',
          'cognito_sub',
        ],
      },
      { subscription: ['billing_profile_id', 'subscription_name', 'account_id'] },
    ],
  },
];

const profileQueryFields = ['gpi_cards', 'gpi_bank_accounts'];

const makePaymentField = ['id'];

class SubscriptionProcessPayment extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    invoices: null,
    invoicesQuery: {
      inProgress: false,
      errorState: false,
      userMessage: null,
    },
    selectedInvoices: [],
    isAllSelected: true,
    bankDetails: [],
    creditCardDetails: [],
    makePaymentErrors: [],
    paymentSuccessFull: false,
    paymentInProgress: false,
    paymentFailed: false,
    showBankDetails: true,
    error: false,
    validationErrors: [],
    showCreditCardForm: false,
    saveCardInfo: false,
    creditCardFeePrice: null,
    creditCardFeeQueryInProgress: false,
    creditCardFeeQueryError: false,
  };

  invoicesDataTableColumns = [
    { id: 'readable_id', heading: 'Invoice #' },
    { id: 'invoice_date', heading: 'Invoice Date' },
    { id: 'invoice_amount', heading: 'Invoice Amount' },
    { id: 'invoice_balance', heading: 'Invoice Balance' },
    { id: 'payment_amount', heading: 'Payment Amount' },
  ];

  invoicesQueryOptions = {
    sortedBy: null,
    limit: null,
    total: null,
    offset: null,
  };

  cognitoSub = null;

  facilityId;
  facility = null;

  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);
    this.facilityId = urlSearchParams.get('facility');
    this.subscriptionId = urlSearchParams.get('subscription');
    this.billingProfileId = null;
  }

  async componentDidMount() {
    const script = document.createElement('script');
    script.src =
      'https://js.paygateway.com/secure_payment/v1/globalpayments.js';
    script.async = true;

    document.body.appendChild(script);
    const initializationTasks = [];
    let settingsResponse = null;
    const facilitySettingsFields = ['use_ach'];
    if (this.facilityId !== null) {
      initializationTasks.push(async () => {
        const response = await getFacility(this.facilityId);
        settingsResponse = await queryGetFacilitySettings(
          {
            facility_id: this.facilityId,
          },
          facilitySettingsFields
        );
        if (response.errors) {
          this.setState({
            pageInitializationComplete: true,
            pageInitializationErrors: response.errors.map(
              ({ message }) => message
            ),
          });
        }
        this.facility = response;
        this.setState({
          showBankDetails: settingsResponse.data.getFacilitySettings.use_ach,
        })
      });

      initializationTasks.push(async () => {
        await this.getInvoices();
      });

      try {
        await Promise.all(initializationTasks.map((task) => task()));
        this.setState({ pageInitializationComplete: true });
      } catch (errors) {
        this.setState({
          pageInitializationComplete: true,
          pageInitializationErrors: errors,
        });
      }
    }
  }

  async getMyProfile() {
    if (this.cognitoSub) {
      const variables = {
        cognitoSub: this.cognitoSub,
      };
      const response = await queryMyProfile(variables, profileQueryFields);
      const bankDetails = response.data.myProfile.gpi_bank_accounts.map(
        (account) => {
          return { ...account, selected: false };
        }
      );
      const creditCardDetails = response.data.myProfile.gpi_cards.map(
        (card, index) => {
          return { ...card, selected: index === 0 };
        }
      );
      this.setState({ bankDetails, creditCardDetails });
    }
  }

  makePayment = async (temporaryToken) => {
    this.setState({
      paymentInProgress: true,
    });

    const { selectedInvoices, bankDetails, creditCardDetails } = this.state;
    const invoices = selectedInvoices.filter((invoice) => invoice.isSelected);
    const paymentMethod =
      bankDetails.filter((account) => account.selected)[0] ||
      creditCardDetails.filter((card) => card.selected)[0];

    const paymentRequests = [];
    invoices.map((invoice) => {
      paymentRequests.push(
        new Promise(async (resolve) => {
          const payload = {
            invoice_id: invoice.id,
            payment_method_id: temporaryToken || paymentMethod.token,
            isAdmin: true,
            allowDuplicate: true,
          }
          if (invoice.balance !== invoice.initialBalance) {
            payload['customPaymentAmount'] = invoice.balance
          }
          if(temporaryToken){
            payload['paymentMethodStored'] = false;
            payload['paymentMethodType'] = 'card';
          }
          const response = await querymakePayment(
            payload,
            makePaymentField
          );
          if (response.errors)
            resolve({
              ...response,
              errors: { ...response.errors[0], id: invoice.readableId },
            });
          resolve(response);
        })
      );
    });

    const response = await Promise.all(paymentRequests);
    const errors = response.filter((resp) => !isEmpty(resp.errors));

    if (!isEmpty(errors)) {
      this.setState({
        paymentInProgress: false,
        paymentSuccessFull: false,
        paymentFailed: true,
        makePaymentErrors: errors.map((resp) => resp.errors),
        showCreditCardForm: false,
      });
    } else {
      this.setState({
        paymentSuccessFull: true,
        paymentInProgress: false,
        showCreditCardForm: false,
      });
    }

    await this.getInvoices();

    setTimeout(async () => {
      this.setState({
        paymentFailed: false,
        makePaymentErrors: [],
      });
    }, 3000);
  };

  setCreditCardFeePrice() {
    const { billingProfileId, subscriptionId, state } = this;
    const { creditCardDetails, selectedInvoices, showCreditCardForm } = state;

    const invoicesSelected = selectedInvoices.filter((invoice) => invoice.isSelected);
    const isCreditCardSelected = showCreditCardForm || creditCardDetails.some((cc) => cc.selected);
    
    if (!isCreditCardSelected || !invoicesSelected.length) {
      return this.setState({ creditCardFeePrice: null });
    };

    const paymentAmount = invoicesSelected.reduce((total, invoice) => total + invoice.balance, 0);

    this.setState({ creditCardFeeQueryInProgress: true }, async () => {
      const { data, errors } = await queryGetCreditCardFeePrice({
        billingProfileId,
        paymentAmount,
        numberOfInvoices: invoicesSelected.length,
        subscriptionId,
      });

      if (errors) {
        console.log(errors);
        return this.setState({
          creditCardFeeQueryInProgress: false,
          creditCardFeeQueryError: true,
        });
      }

      const { price, displayPrice } = data.getCreditCardFeePrice;
      const creditCardFeePrice = (price > 0) ? displayPrice : null;
  
      this.setState({
        creditCardFeePrice,
        creditCardFeeQueryInProgress: false,
      });
    });
  }

  async getInvoices() {
    const invoicesQuery = {
      ...this.state.invoicesQuery,
      inProgress: true,
      errorState: false,
      userMessage: null,
    };

    this.setState({ invoicesQuery }, async () => {
      const options = this.getInvoicesQueryOptions();

      const response = await queryListInvoices(options, invoiceQueryFields);
      if (response.errors) {
        const invoicesQuery = {
          ...this.state.invoicesQuery,
          inProgress: false,
          errorState: true,
          userMessage:
            'An error occurred while retrieving the list of invoices.',
        };
        this.setState({ invoicesQuery });
        return;
      }

      const rawInvoices = response.data.listInvoices.invoices;
      const firstInvoice = rawInvoices[0];
      this.billingProfileId = firstInvoice?.subscription.billing_profile_id;

      this.cognitoSub = firstInvoice?.payor.cognito_sub;
      this.invoicesQueryOptions.total = response.data.listInvoices.total;

      await this.getMyProfile();
      const invoices = this.getInvoicesDataTableRows(rawInvoices);

      const invoicesQuery = {
        ...this.state.invoicesQuery,
        inProgress: false,
        errorState: false,
        userMessage: null,
      };

      this.setState({
        invoices,
        rawInvoices,
        invoicesQuery,
        paymentSuccessFull: false,
        saveCardInfo: false,
      }, () => this.setCreditCardFeePrice());
    });
  }

  getInvoicesDataTableRows(invoices) {
    let rows = [];
    const selectedInvoices = [];
    let totalAmount = 0;
    let totalBalance = 0;

    for (let invoice of invoices) {
      selectedInvoices.push({
        isSelected: true,
        id: invoice.id,
        readableId: invoice.readable_id,
        balance: invoice.balance,
        initialBalance: invoice.balance,
      });
      let row = [];
      for (let column of this.invoicesDataTableColumns) {
        let cell = null;
        switch (column.id) {
          case 'readable_id':
            cell = `<a href="?page=parkchirp_invoice&invoice=${invoice.id}${
              this.facilityId !== null ? `&facility=${this.facilityId}` : ''
            }">${invoice.readable_id}</a>`;
            break;
          case 'invoice_date':
            cell = convertUnixMsToDateInTimezone(
              invoice.created_at,
              this.facility.timezone
            );
            break;
          case 'invoice_amount':
            cell = (invoice.amount / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
            totalAmount += invoice.amount;
            break;
          case 'invoice_balance':
            cell = (invoice.balance / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            });
            totalBalance += invoice.balance;
            break;
          case 'payment_amount':
            cell = invoice.balance
            break;
          default:
        }
        row.push(cell);
      }
      rows.push(row);
    }
    this.setState({
      selectedInvoices,
      isAllSelected: rows.length > 0,
      totalAmount: (totalAmount / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
      totalBalance: (totalBalance / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    });
    return rows;
  }

  getInvoicesQueryOptions() {
    const queryOptions = {};

    const filterExpressions = [
      {
        model: 'INVOICE',
        field: 'balance',
        op: 'GT',
        value: '0',
      },
      {
        model: 'SUBSCRIPTION',
        field: 'id',
        op: 'EQ',
        value: this.subscriptionId,
      },
      {
        model: 'BILLING_PROFILE',
        field: 'facility_id',
        op: 'EQ',
        value: this.facilityId,
      },
    ];

    queryOptions.order = {
      direction: 'ASC',
      field: 'created_at',
      model: 'INVOICE',
    };

    queryOptions.filters = filterExpressions;

    return queryOptions;
  }

  handleSelect = (e, index) => {
    const { invoices, selectedInvoices } = this.state;

    const selectedInvoiceIndex = selectedInvoices.findIndex(invoice => invoice.isSelected);
    if(this.state.showCreditCardForm && selectedInvoiceIndex >= 0) {
      selectedInvoices[selectedInvoiceIndex].isSelected = false
      invoices[selectedInvoiceIndex][4] = 0;
    } 

    selectedInvoices[index].isSelected = e.target.checked;
    invoices[index][4] = e.target.checked ? selectedInvoices[index].balance : 0;

    this.setState({
      invoices,
      selectedInvoices,
      isAllSelected: e.target.checked,
      error: null,
    }, () => this.setCreditCardFeePrice());
  };

  handleSelectAll = (e) => {
    let invoices = this.state.invoices;
    if(this.state.showCreditCardForm) {
      return;
    }
    invoices = invoices.map((invoice, index) => {
      let selectedInvoice = invoice;
      selectedInvoice[selectedInvoice.length - 1] = 
        (e.target.checked ? this.state.selectedInvoices[index].balance : 0)
      return selectedInvoice;
    });
    this.setState({
      invoices,
      selectedInvoices: this.state.selectedInvoices.map((i) => ({
        ...i,
        isSelected: e.target.checked,
      })),
      isAllSelected: e.target.checked,
      error: null,
    }, () => this.setCreditCardFeePrice());
  };

  onInputFieldChange = (index, value) => {
    let invoices = this.state.invoices;
    let selectedInvoices = this.state.selectedInvoices;
    selectedInvoices[index].balance = value
    invoices = invoices.map((invoice, ind) => {
      let selectedInvoice = invoice;
      index === ind && (selectedInvoice[selectedInvoice.length - 1] = value);
      return selectedInvoice;
    });
    let validationErrors = this.state.validationErrors;
    if(value > selectedInvoices[index].initialBalance || value < 1) {
      const existingIds = validationErrors.find(error => error.id === selectedInvoices[index].readableId)
      if(!existingIds?.id) {
        validationErrors.push({ id: selectedInvoices[index].readableId });
      }
    } else {
      validationErrors = validationErrors.filter(error => error.id !== selectedInvoices[index].readableId)
    }
    this.setState({
      invoices,
      selectedInvoices,
      error: !isEmpty(validationErrors),
      validationErrors,
    }, () => this.setCreditCardFeePrice());
  }

  onPaymentMethodSelect = (value, paymentMethod, type) => {
    const { bankDetails, creditCardDetails } = this.state;
    let cards = creditCardDetails;
    let accounts = bankDetails;
    if (type === 'card') {
      cards = creditCardDetails.map((card) => {
        return {
          ...card,
          selected: card.token === paymentMethod.token ? value : false,
        };
      });
      accounts = bankDetails.map((account) => {
        return {
          ...account,
          selected: false,
        };
      });
    } else {
      accounts = bankDetails.map((account) => {
        return {
          ...account,
          selected: account.token === paymentMethod.token ? value : false,
        };
      });
      cards = creditCardDetails.map((card) => {
        return {
          ...card,
          selected: false,
        };
      });
    }

    this.setState({
      bankDetails: accounts,
      creditCardDetails: cards,
    }, () => this.setCreditCardFeePrice());
  };

  handleClickCreditCardForm = (selectedPaymentMethod) => {
    const { creditCardDetails, showCreditCardForm } = this.state;
    this.handleSelectAll({ target: { checked: false } });
    this.setState({
      showCreditCardForm: !showCreditCardForm,
      creditCardDetails: creditCardDetails.map((card, index) => {
        return {
          ...card,
          selected: selectedPaymentMethod?.token === card.token ? showCreditCardForm : index === 0 ? true : false
        }
      }),
    }, () => this.setCreditCardFeePrice());
  }

  renderPanelDataTable() {
    const { invoices, selectedInvoices, isAllSelected, invoicesQuery } =
      this.state;

    if (invoicesQuery.inProgress) {
      return (
        <div className='parkchirp--page--user-message-container'>
          <ProcessingIndicator userMessage='Retrieving Invoices...' />
        </div>
      );
    }

    if (invoicesQuery.errorState) {
      return (
        <div className='parkchirp--page--user-message-container'>
          <p>{invoicesQuery.userMessage}</p>
        </div>
      );
    }

    if (invoices !== null) {
      return (
        <React.Fragment>
          <DataTable
            id='invoice-database'
            columns={this.invoicesDataTableColumns}
            rows={invoices}
            selectedItems={selectedInvoices}
            isAllSelected={isAllSelected}
            showCheckbox={invoices.length > 0}
            editable={true}
            displayDataTablelimit={true}
            noRecordsText={
              invoices.length === 0 &&
              "All of this subscription's invoices have been paid"
            }
            overallRowCount={this.invoicesQueryOptions.total}
            currentOffset={this.invoicesQueryOptions.offset}
            currentLimit={this.invoicesQueryOptions.limit}
            sortedBy={this.invoicesQueryOptions.sortedBy}
            onSelectAll={this.handleSelectAll}
            onItemCheck={this.handleSelect}
            onInputFieldChange={this.onInputFieldChange}
          />
        </React.Fragment>
      );
    }
  }

  renderPaymentOptions() {
    const {
      invoices,
      selectedInvoices,
      totalAmount,
      totalBalance,
      creditCardDetails,
      bankDetails,
      paymentInProgress,
      paymentSuccessFull,
      makePaymentErrors,
      paymentFailed,
      showBankDetails,
      validationErrors,
      error,
      showCreditCardForm,
      saveCardInfo,
      invoicesQuery: {
        inProgress: invoicesQueryInProgress,
      },
      creditCardFeePrice,
      creditCardFeeQueryInProgress,
      creditCardFeeQueryError,
    } = this.state;

    const creditCardFeeDetails = {
      creditCardFeePrice,
      creditCardFeeQueryInProgress,
      creditCardFeeQueryError,
    };

    const tableClasses = [
      'parkchirp--data-table--table',
      'widefat',
      'fixed',
      'striped',
    ];

    let totalPaymentAmount = 0;
    let isInvoiceSelected;
    const isPaymentMethodSelected =
      bankDetails.find((account) => account.selected) ||
      creditCardDetails.find((card) => card.selected);

    totalPaymentAmount = selectedInvoices.reduce((amount, invoice) => {
      if (invoice.isSelected) {
        isInvoiceSelected = true;
        amount += invoice.balance;
      }

      return amount;
    }, 0);

    totalPaymentAmount = (totalPaymentAmount / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    const disableMakePayment = (
      invoicesQueryInProgress ||
      !isInvoiceSelected ||
      !isPaymentMethodSelected ||
      error ||
      creditCardFeeQueryInProgress ||
      creditCardFeeQueryError
    );

    return (
      <React.Fragment>
        {invoices && invoices.length > 0 && (
          <>
            <div className='parkchirp--page--subscription-process--payment-panel-total-column'>
              <table className={tableClasses.join(' ')}>
                <thead>
                  <tr>
                    <th></th>
                    <th></th>
                    <th className='total-column'>Total</th>
                    <th>{totalAmount}</th>
                    <th>{totalBalance}</th>
                    <th>{totalPaymentAmount}</th>
                  </tr>
                </thead>
              </table>
            </div>
            <div>
              <h3>Payment Options</h3>

              <div className='parkchirp--page--subscription-process--payment-panel-payment-options'>
                <div className='payment-options-credit-card-container'>
                  <h5>Credit Card</h5>
                  <div className='payment-options'>
                    {creditCardDetails &&
                      creditCardDetails.map((card) => (
                        <div>
                          <input
                            checked={card.selected}
                            onChange={(e) =>
                              this.onPaymentMethodSelect(
                                e.target.checked,
                                card,
                                'card'
                              )
                            }
                            type='checkbox'
                            disabled={showCreditCardForm}
                          />
                          <span>
                            {card.type} ending in {card.last4}, {card.exp_month}
                            /{card.exp_year}
                          </span>
                        </div>
                      ))}
                  </div>
                  <div className='parkchirp--button-add-new'>
                    <button className='parkchirp--button-as-link' onClick={() => 
                      this.handleClickCreditCardForm(isPaymentMethodSelected)
                    }>
                      {showCreditCardForm ? 'Use a existing card' : 'Use a different card'}
                    </button>
                  </div>
                </div>
                { showBankDetails && 
                  <div className='payment-options-bank-account-container'>
                    <h5>Bank Account</h5>
                    <div className='payment-options'>
                      {bankDetails &&
                        bankDetails.map((account) => (
                          <div>
                            <input
                              checked={account.selected}
                              onChange={(e) =>
                                this.onPaymentMethodSelect(
                                  e.target.checked,
                                  account,
                                  'bank'
                                )
                              }
                              type='checkbox'
                              disabled={showCreditCardForm}
                            />
                            <span>
                              {account.accountType} account ending in{' '}
                              {account.last4}
                            </span>
                          </div>
                        ))}
                    </div>
                    <div className='parkchirp--button-add-new'>
                      <button className='parkchirp--button-as-link'>
                        Add New
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
            <div className='parkchirp-credit-card-form'>
              {showCreditCardForm && 
              <>
                <div className='parkchirp--make-payment-note-text'>
                  <NoticeBlock
                    type='error'
                    containerClasses={['parkchirp--sidebar-block']}
                    >
                      <p>Only one invoice can be paid.</p>
                  </NoticeBlock>
                </div>
                <GPICreditCardForm
                  cognitoSub={this.cognitoSub}
                  getCreditCards={(temporaryToken) => this.makePayment(temporaryToken)}
                  isViewOnly={false}
                  showCreditCardForm={showCreditCardForm}
                  saveCardInfo={saveCardInfo}
                  creditCardFeeDetails={creditCardFeeDetails}
                />
                <div>
                  <input
                    type="checkbox"
                    checked={saveCardInfo}
                    onChange={(e) => {
                      this.setState({saveCardInfo: e.target.checked})
                    }}
                  />
                  <span>Save card for future use</span>
                </div>
              </>
              }
            </div>
            <div className='parkchirp--make-payment-section'>
              {!showCreditCardForm && (
                <>
                  <CreditCardFeeDisclaimer
                    creditCardFeePrice={creditCardFeePrice}
                    creditCardFeeQueryInProgress={creditCardFeeQueryInProgress}
                    creditCardFeeQueryError={creditCardFeeQueryError}
                  />
                  <ActionButton
                    id='parkchirp--make-payment-action-button'
                    label='Make Payment'
                    inProgressState={paymentInProgress}
                    onClick={() => this.makePayment()}
                    disabled={disableMakePayment}
                  />
                </>
              )}
              {paymentSuccessFull && (
                <div className='parkchirp--make-payment-success-text'>
                  <NoticeBlock
                    type='message'
                    containerClasses={['parkchirp--sidebar-block']}
                  >
                    <p>The Payment has been processed.</p>
                  </NoticeBlock>
                </div>
              )}
              {paymentFailed && (
                <div className='parkchirp--make-payment-success-text'>
                  <NoticeBlock
                    type='error'
                    containerClasses={['parkchirp--sidebar-block']}
                  >
                    {makePaymentErrors.map((error) => (
                      <p>
                        {error.message} Invoice Id: {error.id}
                      </p>
                    ))}
                  </NoticeBlock>
                </div>
              )}
              {error && (
                <div className='parkchirp--make-payment-success-text'>
                  <NoticeBlock
                    type='error'
                    containerClasses={['parkchirp--sidebar-block']}
                  >
                    <p>Please check Payment Amount for below Invoices<br />
                    {validationErrors.map((error) => (
                        <><span>{error.id}</span><br /></>
                    ))}
                    </p>
                  </NoticeBlock>
                </div>
              )}
            </div>
          </>
        )}
      </React.Fragment>
    );
  }

  render() {
    return (
      <div className='parkchirp--page--subscription-process--payment-panel'>
        {this.renderPanelDataTable()}
        {this.renderPaymentOptions()}
      </div>
    );
  }
}
export default SubscriptionProcessPayment;
